import { useCallback, useEffect, useRef, useState } from "react";

import {
  NO_VALUE_OPERATORS,
  OPERATORS_BY_TYPE,
  RANGE_VALUE_OPERATORS,
} from "./operators";
import { specToCondition } from "./specToCondition";
import type {
  FilterCondition,
  FilterSpec,
  FilterType,
  UiOperator,
} from "./types";

export interface UseColumnFilterParams {
  columnId: string;
  filterType: FilterType;
  /** The column's applied filter (editor state re-syncs from it on open). */
  spec: FilterSpec | null;
  isOpen: boolean;
  /** Override the operator choices (defaults to the full set for the type). */
  operators?: UiOperator[];
}

export interface UseColumnFilterReturn {
  operator: UiOperator;
  setOperator: (operator: UiOperator) => void;
  operatorOptions: UiOperator[];
  value: string;
  setValue: (value: string) => void;
  /** Second value for between/not between operators */
  value2: string;
  setValue2: (value: string) => void;
  /** True if the operator takes no value (is blank / is not blank) — the value input is disabled. */
  takesNoValue: boolean;
  /** True if operator expects a range with two values (between / not between) */
  usesRangeValue: boolean;
  /** AND/OR join between condition 1 and the (optional) second condition. */
  join: "and" | "or";
  setJoin: (join: "and" | "or") => void;
  /** Operator for the second, optional condition. */
  secondOperator: UiOperator;
  setSecondOperator: (operator: UiOperator) => void;
  secondValue: string;
  setSecondValue: (value: string) => void;
  secondValue2: string;
  setSecondValue2: (value: string) => void;
  /**
   * True once condition 1 has content (a non-empty trimmed value, or a
   * no-value operator) — the second-condition row is revealed once the user
   * has started filling in the first.
   */
  showSecond: boolean;
  /** True if the second operator takes a value (mirrors `!takesNoValue`). */
  secondUsesValue: boolean;
  /** True if the second operator expects a range with two values. */
  secondUsesRangeValue: boolean;
  /**
   * Build the spec for the current editor state. `null` means "clear the
   * filter" (empty/incomplete value); `undefined` means the input doesn't
   * parse for this column's type (invalid — don't commit).
   */
  buildSpec: () => FilterSpec | null | undefined;
}

/** Assemble a condition from editor state, normalizing a no-value operator to
 *  `value: ""` and dropping `value2` for non-range operators. */
const buildCondition = (
  operator: UiOperator,
  value: string,
  value2: string
): FilterCondition =>
  NO_VALUE_OPERATORS.has(operator)
    ? { operator, value: "" }
    : {
        operator,
        value,
        value2: RANGE_VALUE_OPERATORS.has(operator) ? value2 : undefined,
      };

export function useColumnFilter({
  columnId,
  filterType,
  spec,
  isOpen,
  operators,
}: UseColumnFilterParams): UseColumnFilterReturn {
  const operatorOptions = operators ?? OPERATORS_BY_TYPE[filterType];
  const defaultOperator: UiOperator = operatorOptions[0] ?? "=";
  const [operator, setOperator] = useState<UiOperator>(
    spec?.operator ?? defaultOperator
  );
  const [value, setValue] = useState<string>(spec?.value ?? "");
  const [value2, setValue2] = useState<string>(spec?.value2 ?? "");

  const [join, setJoin] = useState<"and" | "or">(spec?.join ?? "and");
  const [secondOperator, setSecondOperator] = useState<UiOperator>(
    spec?.second?.operator ?? defaultOperator
  );
  const [secondValue, setSecondValue] = useState<string>(
    spec?.second?.value ?? ""
  );
  const [secondValue2, setSecondValue2] = useState<string>(
    spec?.second?.value2 ?? ""
  );

  // Track the previous columnId to detect when we switch to a different filter
  const prevColumnIdRef = useRef(columnId);

  // Sync state when closed OR when switching to a different column while
  // opening. Because closing re-syncs from the applied spec, edits abandoned
  // via click-outside are discarded.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const columnChanged = prevColumnIdRef.current !== columnId;
    prevColumnIdRef.current = columnId;

    if (!isOpen || columnChanged) {
      setOperator(spec?.operator ?? defaultOperator);
      setValue(spec?.value ?? "");
      setValue2(spec?.value2 ?? "");
      setJoin(spec?.join ?? "and");
      setSecondOperator(spec?.second?.operator ?? defaultOperator);
      setSecondValue(spec?.second?.value ?? "");
      setSecondValue2(spec?.second?.value2 ?? "");
    }
  }, [spec, defaultOperator, isOpen, columnId]);

  const takesNoValue = NO_VALUE_OPERATORS.has(operator);
  const showSecond = takesNoValue || value.trim() !== "";

  const secondTakesNoValue = NO_VALUE_OPERATORS.has(secondOperator);

  const buildSpec = useCallback((): FilterSpec | null | undefined => {
    const primary = buildCondition(operator, value, value2);

    // A half-filled second range (missing its end) is inert — commit a plain
    // single-condition spec rather than a pair that compiles to the primary
    // alone.
    const secondHasContent =
      secondTakesNoValue ||
      (secondValue.trim() !== "" &&
        (!RANGE_VALUE_OPERATORS.has(secondOperator) ||
          secondValue2.trim() !== ""));

    const candidate: FilterSpec =
      showSecond && secondHasContent
        ? {
            ...primary,
            join,
            second: buildCondition(secondOperator, secondValue, secondValue2),
          }
        : primary;

    // The wire compiler is the single authority on emptiness (null = clear)
    // and validity (undefined = don't commit); the future filtrex target
    // shares the same input-parsing rules.
    const compiled = specToCondition(columnId, filterType, candidate);
    if (compiled === undefined) return undefined;
    if (compiled === null) return null;
    return candidate;
  }, [
    columnId,
    filterType,
    operator,
    value,
    value2,
    showSecond,
    join,
    secondOperator,
    secondValue,
    secondValue2,
    secondTakesNoValue,
  ]);

  return {
    operator,
    setOperator,
    operatorOptions,
    value,
    setValue,
    value2,
    setValue2,
    takesNoValue,
    usesRangeValue: RANGE_VALUE_OPERATORS.has(operator),
    join,
    setJoin,
    secondOperator,
    setSecondOperator,
    secondValue,
    setSecondValue,
    secondValue2,
    setSecondValue2,
    showSecond,
    secondUsesValue: !secondTakesNoValue,
    secondUsesRangeValue: RANGE_VALUE_OPERATORS.has(secondOperator),
    buildSpec,
  };
}
